<template>
    <div class="p-4 space-y-4 text-neutral-base w-full">
        <div class="w-full flex items-center space-x-4">
            <UIAvatar
                :avatar="content.creator"
                class="shrink-0"
            />
            <div class="flex-1 shrink min-w-0">
                <p class="content-sm-ux-medium truncate">
                    {{ content.creator_name }}
                </p>
                <p class="content-xs-ux-regular truncate">
                    {{ formattedDate }}
                </p>
            </div>
        </div>
        <div
            v-if="content.body"
            class="content-sm-ux-regular text-neutral-subtle"
        >
            <!-- Remove the prose max-width if there's a table in the content -->
            <UIHtml
                :html="content.body"
                class="w-full prose has-[table,iframe]:max-w-none leading-5 text-[length:inherit] text-inherit"
                style="overflow-wrap: anywhere;"
            />
        </div>
        <div
            v-for="({ content, images }, index) in contentWithImages"
            :key="index"
            :data-cy-learning-moment-content="content.type"
        >
            <template v-if="images && images.length > 0">
                <button
                    type="button"
                    class="appearance-none relative mt-4 mb-2 w-full cursor-pointer"
                    aria-label="View image"
                    @click="handleImageClick(content, images, 0)"
                >
                    <div
                        class="shrink-0 relative overflow-hidden rounded-xl before:rounded-xl "
                        :class="{
                            'before:rounded-xl before:content-[\'\'] before:absolute before:inset-0 before:bg-gray-200 before:animate-pulse aspect-[calc(382/200)]': !imagesLoaded[images[0].url],
                        }"
                    >
                        <img
                            :src="images[0].url"
                            alt=""
                            class="w-full object-cover shrink-0 h-full max-h-[400px]"
                            @load="imagesLoaded[images[0].url] = true"
                        >
                    </div>
                    <div
                        v-if="content.type === studentLearningMomentsContentType.video"
                        class="absolute inset-0 flex items-center justify-center z-20"
                    >
                        <DButton
                            class="pointer-events-none"
                            size="md"
                            icon="ph:play-fill"
                        />
                    </div>
                </button>
                <div v-if="images.length > 1">
                    <div class="grid grid-cols-3 gap-2">
                        <button
                            v-for="(image, index) in images.slice(1)"
                            :key="index"
                            type="button"
                            class="appearance-none w-full aspect-square bg-cover bg-center bg-no-repeat cursor-pointer rounded-xl"
                            :style="{ backgroundImage: `url(${image.url})` }"
                            :aria-label="`View image ${index + 2}`"
                            @click="handleImageClick(content, images, index + 1)"
                        />
                    </div>
                </div>
            </template>
            <template v-else-if="content.type === studentLearningMomentsContentType.file">
                <button
                    type="button"
                    class="appearance-none w-full mt-6 border border-neutral-base rounded-lg overflow-hidden cursor-pointer"
                    @click="openExternalUrl(content.url)"
                >
                    <div class="px-4 py-3.5 flex items-center transition-colors duration-150 hover:bg-gray-100 active:bg-gray-100">
                        <div class="space-y-1 flex-1">
                            <p
                                class="text-neutral-strong font-medium text-sm flex items-center justify-between"
                                style="overflow-wrap: anywhere;"
                            >
                                <span>{{ content.filename }}</span>
                            </p>
                        </div>

                        <div class="ml-auto pl-2">
                            <PhIcon
                                icon="download-simple"
                                class="text-(--color-brand-700)"
                                :size="24"
                                weight="regular"
                            />
                        </div>
                    </div>
                </button>
            </template>
            <template v-else-if="content.type === studentLearningMomentsContentType.audio">
                <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
                <audio
                    :src="content.url"
                    controls
                    class="w-full"
                >
                    Your browser does not support the audio element.
                </audio>
            </template>
        </div>
        <div
            v-if="content.tags?.length > 0"
            class="content-xs-ux-regular italic"
        >
            {{ content.tags.join(', ') }}
        </div>
        <div
            v-if="content.comments"
            class="-mx-4"
        >
            <Comments
                :config="content.comments"
            />
        </div>
    </div>
</template>

<script setup>
    import mitt from 'mitt'
    import { useRouter } from 'vue-router'
    import dayjs from '@/dayjs'
    import { DButton } from '@digistorm/spark'

    import linkType from '@/constants/linkType'
    import studentLearningMomentsContentType from '@/constants/studentLearningMomentsContentType'
    import UIHtml from '@/components/ui/UIHtml.vue'
    import Comments from '@/components/modules/Comments.vue'
    import widgetType from '@/constants/widgetType'

    const props = defineProps({
        config: {
            type: Object,
            required: true,
        },
    })

    const widgetsStore = useWidgetsStore()
    const router = useRouter()
    const moduleHelpers = useModuleHelpers()
    const { openGallery } = useGallery()

    const contextualEvents = inject('contextualEvents', ref(mitt()))
    provide('contextualEvents', contextualEvents)

    const imagesLoaded = ref({})

    const content = computed(() => props.config.data)

    const date = computed(() => {
        return dayjs(content.value.date)
    })

    const formattedDate = computed(() => {
        return dayjs(date.value).fromNow()
    })

    const images = (content) => {
        if (content.type === studentLearningMomentsContentType.image) {
            return [
                {
                    filename: content.filename,
                    url: content.url,
                },
            ]
        }
        if (content.type === studentLearningMomentsContentType.video) {
            return [
                {
                    filename: content.filename,
                    url: content.image_url,
                },
            ]
        }
        if (content.type === studentLearningMomentsContentType.gallery) {
            return content.images
        }
        return []
    }

    const contentWithImages = computed(() => {
        return map(content.value.content, (content) => {
            return {
                images: images(content),
                content,
            }
        })
    })

    const openExternalUrl = (url) => {
        moduleHelpers.openModuleOrOpenBrowserForLinkData(router, {
            type: linkType.external,
            url,
            open_externally: true,
        })
    }

    const handleImageClick = (content, images, index) => {
        if (content.type === studentLearningMomentsContentType.video) {
            openExternalUrl(content.url)
            return
        }

        openGallery({
            images: map(images, (image) => ({
                description: image.filename,
                url: image.url,
                thumbnail: image.url,
                focus_x: 50,
                focus_y: 50,
            })),
            start: index,
        })
    }

    // This refresh functionality is so that comments can trigger an update of the learning moment
    const refreshDependency = () => {
        // Mark all widget dependencies for student learning moments for refresh
        widgetsStore.markDependenciesForRefresh(widgetType.studentLearningMoments)
    }

    onMounted(() => {
        contextualEvents.value.on('refresh', refreshDependency)
    })

    onUnmounted(() => {
        contextualEvents.value.off('refresh', refreshDependency)
    })
</script>
